import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import {
  ApiPaymentProvider,
  ApiProviderCurrency,
} from '../../../../../core/models/payment-api.models';
import { PaymentApiService } from '../../../../../core/services/payment-api.service';
import { AuthService } from '../../../../../core/services/auth.service';
import { ImageApiService } from '../../../../../core/services/image-api.service';
import { extractApiErrorMessage } from '../../../../../core/utils/api-error.util';

interface CurrencyOption {
  id: string;
  label: string;
}

@Component({
  selector: 'app-subscription-payment',
  templateUrl: './subscription-payment.component.html',
  styleUrls: ['./subscription-payment.component.scss'],
})
export class SubscriptionPaymentComponent implements OnInit {
  @Input() amount = 0;
  @Input() currencyLabel = 'FCFA';
  @Input() policyNumber = '';
  @Input() productId = '';
  @Input() productName = '';
  @Input() description = 'paiement prime assurance';

  @Output() paymentCompleted = new EventEmitter<string>();

  providers: ApiPaymentProvider[] = [];
  selectedProvider: ApiPaymentProvider | null = null;
  selectedCurrencyId = '';
  currencyOptions: CurrencyOption[] = [];
  displayAmount = '';
  loadingProviders = true;
  paying = false;
  loadError = '';
  pendingMessage = '';
  errorModalMessage: string | null = null;
  errorModalCanRetry = false;

  private errorModalRetry: (() => void) | null = null;

  private readonly imageById = new Map<string, string>();

  readonly form = this.fb.group({
    phoneNumber: ['', [Validators.required, Validators.minLength(8)]],
  });

  constructor(
    private readonly fb: FormBuilder,
    private readonly paymentApi: PaymentApiService,
    private readonly authService: AuthService,
    private readonly imageApi: ImageApiService,
  ) {}

  ngOnInit(): void {
    const profile = this.authService.getProfile();
    if (profile?.phone) {
      this.form.patchValue({ phoneNumber: profile.phone });
    }
    this.displayAmount = this.formatAmount(this.amount);
    void this.loadProviders();
  }

  get summaryAmountLabel(): string {
    return `${this.formatAmount(this.amount)} ${this.currencyLabel.toUpperCase()}`;
  }

  get canPay(): boolean {
    return Boolean(
      this.selectedProvider &&
        this.form.valid &&
        !this.paying &&
        !this.loadingProviders &&
        this.amount > 0,
    );
  }

  async loadProviders(): Promise<void> {
    this.loadingProviders = true;
    this.loadError = '';
    try {
      const [providers, images] = await Promise.all([
        firstValueFrom(this.paymentApi.fetchProviders()),
        firstValueFrom(this.imageApi.list()),
      ]);

      for (const image of images) {
        this.imageById.set(image.id, image.url);
      }

      this.providers = providers;
    } catch (err) {
      console.error('[SubscriptionPayment] providers failed', err);
      this.loadError = extractApiErrorMessage(err, 'Impossible de charger les moyens de paiement.');
      this.showErrorModal(this.loadError, () => {
        void this.loadProviders();
      });
    } finally {
      this.loadingProviders = false;
    }
  }

  selectProvider(provider: ApiPaymentProvider): void {
    this.selectedProvider = provider;
    this.pendingMessage = '';
    this.currencyOptions = this.buildCurrencyOptions(provider);
    this.selectedCurrencyId = this.resolveCurrencyId(provider);
    this.displayAmount = this.formatAmount(this.resolvePayAmount(provider, this.amount));
  }

  onCurrencyChange(currencyId: string): void {
    this.selectedCurrencyId = currencyId;
    if (this.selectedProvider) {
      this.displayAmount = this.formatAmount(
        this.resolvePayAmount(this.selectedProvider, this.amount),
      );
    }
  }

  isSelected(provider: ApiPaymentProvider): boolean {
    return this.selectedProvider?.id === provider.id;
  }

  providerLogoUrl(provider: ApiPaymentProvider): string | null {
    if (!provider.imageId) {
      return null;
    }
    return this.imageById.get(provider.imageId) ?? null;
  }

  providerBrandClass(provider: ApiPaymentProvider): string {
    const name = provider.name.toLowerCase();
    if (name.includes('orange')) {
      return 'payment-provider__logo--orange';
    }
    if (name.includes('mtn')) {
      return 'payment-provider__logo--mtn';
    }
    return '';
  }

  providerDisplayName(provider: ApiPaymentProvider): string {
    return provider.name.trim().toLowerCase();
  }

  providerInitials(provider: ApiPaymentProvider): string {
    const name = provider.name.trim();
    return name.slice(0, 3).toUpperCase();
  }

  providerDescription(provider: ApiPaymentProvider): string {
    if (provider.description?.trim()) {
      return provider.description.trim();
    }
    const name = provider.name.toLowerCase();
    if (name.includes('orange')) {
      return 'Payez facilement avec votre compte Orange Money.';
    }
    if (name.includes('mtn')) {
      return 'Payez facilement avec votre compte MTN Mobile Money.';
    }
    return `Payez facilement avec ${provider.name}.`;
  }

  async submitPayment(): Promise<void> {
    if (!this.canPay || !this.selectedProvider) {
      return;
    }

    this.paying = true;
    this.pendingMessage = '';

    try {
      // APIs Orsys de paiement indisponibles : comme le mobile
      // (`payment_widget.dart` / `handlePaymentProcess`), on saute le pay
      // et on enchaîne directement sur l’affaire nouvelle (AFNV).
      this.paymentCompleted.emit('result.reference');

      // const userId = this.authService.getUserId();
      // if (!userId) {
      //   this.showErrorModal('Session expirée. Reconnectez-vous pour payer.');
      //   return;
      // }
      //
      // const phoneNumber = String(this.form.value.phoneNumber ?? '').trim();
      // const provider = this.selectedProvider;
      // const currencyId = this.selectedCurrencyId || this.resolveCurrencyId(provider);
      // const payAmount = this.resolvePayAmount(provider, this.amount);
      // const result: ApiUniversalPayResponse = await firstValueFrom(
      //   this.paymentApi.pay({
      //     currencyd: currencyId,
      //     issuerId: userId,  
      //     amount: payAmount,
      //     baseAmount: this.amount,
      //     providerId: provider.id,
      //     accountNumber: phoneNumber,
      //     productId: this.productId,
      //     productName: this.productName,
      //     description: this.description || 'paiement prime assurance',
      //     phoneNumber,
      //     projectNumber: this.policyNumber,
      //   }),
      // );
      //
      // if (result.status === 'PENDING') {
      //   this.pendingMessage =
      //     result.message || 'Votre paiement est en cours de traitement.';
      //   return;
      // }
      //
      // if (result.status === 'ABORTED') {
      //   this.showErrorModal(result.message || "Délai d'attente dépassé.", () => {
      //     void this.submitPayment();
      //   });
      //   return;
      // }
      //
      // if (result.status !== 'SUCCESSFULL') {
      //   this.showErrorModal(result.message || "Le paiement n'a pas abouti.", () => {
      //     void this.submitPayment();
      //   });
      //   return;
      // }
      //
      // this.paymentCompleted.emit(result.reference);
    } catch (err: unknown) {
      this.paying = false;
      console.error('[SubscriptionPayment] pay failed', err);
      this.showErrorModal(
        extractApiErrorMessage(err, 'Échec du paiement. Réessayez.'),
        () => {
          void this.submitPayment();
        },
      );
    }
  }

  private buildCurrencyOptions(provider: ApiPaymentProvider): CurrencyOption[] {
    const currencies: ApiProviderCurrency[] = [...(provider.providerCurrencies ?? [])];
    currencies.sort((a, b) => Number(this.isDefaultProviderCurrency(b)) - Number(this.isDefaultProviderCurrency(a)));

    return currencies
      .map((item) => ({
        id: String(item.currency?.id ?? item.id ?? ''),
        label: String(item.currency?.shortCode ?? this.currencyLabel).toUpperCase(),
      }))
      .filter((option) => option.id);
  }

  private resolveCurrencyId(provider: ApiPaymentProvider): string {
    const currencies: ApiProviderCurrency[] = provider.providerCurrencies ?? [];
    const selected =
      currencies.find((item) => this.isDefaultProviderCurrency(item)) ?? currencies[0];
    return String(selected?.currency?.id ?? selected?.id ?? '');
  }

  private isDefaultProviderCurrency(item: ApiProviderCurrency): boolean {
    const raw = (item as Record<string, unknown>)['defaulte'] ?? (item as Record<string, unknown>)['default'];
    return raw === true || raw === 'true' || raw === 1 || raw === '1';
  }

  private resolvePayAmount(provider: ApiPaymentProvider, baseAmount: number): number {
    const currencyId = this.selectedCurrencyId || this.resolveCurrencyId(provider);
    const providerCurrencies: ApiProviderCurrency[] = provider.providerCurrencies ?? [];
    const providerCurrency =
      providerCurrencies.find((item) => item.currency?.id === currencyId) ??
      providerCurrencies[0];

    let amount = baseAmount;
    const rates = providerCurrency?.currency?.rates ?? [];
    if (rates.length && currencyId) {
      const rateEntry =
        rates.find((entry) => entry.from?.id === currencyId) ?? rates[0];
      const rate = Number(rateEntry?.rate ?? 1);
      if (!Number.isNaN(rate) && rate > 0) {
        amount = baseAmount * rate;
      }
    }

    if (provider.alwaysRoundToWholeNumbers) {
      amount = Math.ceil(amount);
    }

    return amount;
  }

  private formatAmount(value: number): string {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  closeErrorModal(): void {
    this.errorModalMessage = null;
    this.errorModalCanRetry = false;
    this.errorModalRetry = null;
  }

  retryErrorModal(): void {
    const retry = this.errorModalRetry;
    this.closeErrorModal();
    retry?.();
  }

  private showErrorModal(message: string, retry?: () => void): void {
    const text = (message || '').trim();
    if (!text) {
      return;
    }
    this.errorModalMessage = text;
    this.errorModalCanRetry = Boolean(retry);
    this.errorModalRetry = retry ?? null;
  }
}
